import React from 'react'
import {
	components,
	InputProps,
	MenuListProps,
	OptionProps,
	PropsValue,
} from 'react-select'

import { SelectOption } from '@/components'
import { useAppContext } from '@/hooks'
import { hasDataTestIdInProps } from '@/lib/components/Select/utils'
import { FormValue } from '@/types'

const { Option, MenuList, Input } = components

function normalizeToArray(input: unknown) {
	if (Array.isArray(input)) {
		return input
	}

	if (input != null) {
		return [input]
	}

	return []
}

export const getSelectValue = (
	getOptionValue: (option: SelectOption) => string,
	options?: SelectOption[] | undefined,
	value?: FormValue,
	isMulti?: boolean | undefined,
): PropsValue<SelectOption> | undefined => {
	if (!options?.length) {
		return null
	}

	if (isMulti && Array.isArray(value)) {
		const filteredOptions = options.filter((opt) => {
			return !!value.find((val) => val === getOptionValue(opt))
		})
		return filteredOptions.length > 0 ? filteredOptions : null
	}

	return value ? options?.find((opt) => getOptionValue(opt) === value) : null
}
// Custom Option component that shows the name in bold and the full path in regular text with a tooltip
export const CustomOption = (props: OptionProps<any>) => {
	const { t } = useAppContext()

	return (
		<Option {...props}>
			<div className="flex flex-col">
				<div className="font-bold break-words">
					{props.data.name || t('NOT_AVAILABLE_SHORT')}
				</div>
				<div title={props.data.fullPath} className="text-sm break-all">
					{props.data.fullPath}
				</div>
			</div>
		</Option>
	)
}

export const CustomMenuList = (props: MenuListProps<any, boolean, any>) => {
	const { children, selectProps } = props
	const { value, isMulti, getOptionValue } = selectProps
	const { t } = useAppContext()

	const selectedValues = isMulti
		? normalizeToArray(value)
		: value
			? [value]
			: []
	const selectedValueIds = selectedValues.map((val: any) =>
		typeof getOptionValue === 'function'
			? getOptionValue(val)
			: val?.value || val,
	)

	const selectedOptions: React.ReactNode[] = []
	const unselectedOptions: React.ReactNode[] = []

	React.Children.forEach(children, (child) => {
		if (React.isValidElement(child) && child.props?.data) {
			const optionValue =
				typeof getOptionValue === 'function'
					? getOptionValue(child.props.data)
					: child.props.data.value || child.props.data

			if (selectedValueIds.includes(optionValue)) {
				selectedOptions.push(child)
			} else {
				unselectedOptions.push(child)
			}
		} else {
			unselectedOptions.push(child)
		}
	})

	return (
		<MenuList {...props}>
			{selectedOptions.length > 0 && (
				<>
					<div className="px-3 text-xs font-semibold text-gray-600 uppercase bg-blue-50 border-b border-blue-200 z-10">
						{t('SELECT_SELECTED')} ({selectedOptions.length})
					</div>
					<div>{selectedOptions}</div>
					{unselectedOptions.length > 0 && (
						<div className="border-t border-gray-300" />
					)}
				</>
			)}

			{unselectedOptions.length > 0 && (
				<>
					{selectedOptions.length > 0 && (
						<div className="px-3 py-1 text-xs font-semibold text-gray-600 uppercase bg-gray-50 z-10 -mt-px">
							{t('AVAILABLE')} ({unselectedOptions.length})
						</div>
					)}
					<div>{unselectedOptions}</div>
				</>
			)}
		</MenuList>
	)
}

/**
 * Restore Home/End cursor behavior in the input (react-select uses them for
 * first/last option focus). Call from input onKeyDown for Home/End.
 */
function handleSelectInputHomeEnd(e: React.KeyboardEvent<HTMLInputElement>) {
	if (e.key !== 'Home' && e.key !== 'End') return
	const input = e.target as HTMLInputElement
	const len = input.value.length
	e.preventDefault()
	e.stopPropagation()
	if (e.key === 'Home') {
		if (e.shiftKey) {
			input.setSelectionRange(0, input.selectionStart ?? 0)
		} else {
			input.setSelectionRange(0, 0)
		}
	} else {
		if (e.shiftKey) {
			input.setSelectionRange(input.selectionEnd ?? len, len)
		} else {
			input.setSelectionRange(len, len)
		}
	}
}

export const DefaultInputWithTestId = ({
	onKeyDown: propsOnKeyDown,
	...rest
}: InputProps<SelectOption>) => {
	const testId = hasDataTestIdInProps(rest.selectProps)
		? rest.selectProps['data-testid']
		: undefined

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Home' || e.key === 'End') {
			handleSelectInputHomeEnd(e)
			return
		}
		propsOnKeyDown?.(e)
	}

	return (
		<Input
			{...rest}
			onKeyDown={onKeyDown}
			data-testid={`select-input-${testId}`}
		/>
	)
}
